/**
 * XLSX reader (Faz 8.7, OCS data_link.rs parity via SheetJS community
 * build 0.18.5 — LOCKED version, local file reading only, no network).
 *
 * Parity surface:
 *  - workbook_metadata: sheet names + named ranges (OCS workbook_metadata)
 *  - workbook_rows: sheet + range_kind (sheet/named) + range_value walk
 *  - '$' cleanup (OCS strips currency marks from formatted numbers)
 */

import * as XLSX from 'xlsx';

export interface WorkbookMeta {
    sheets: string[];
    /** Named ranges: name → "Sheet!A1:D20" definition. */
    namedRanges: Record<string, string>;
}

export type RangeKind = 'sheet' | 'named';

/** Parse a workbook from file bytes (xlsx/xls/csv — SheetJS sniffs). */
function loadWorkbook(data: ArrayBuffer): XLSX.WorkBook {
    return XLSX.read(data, { type: 'array', cellDates: false });
}

/** Workbook metadata: sheet list + named-range definitions. */
export function workbookMeta(data: ArrayBuffer): WorkbookMeta {
    const wb = loadWorkbook(data);
    const namedRanges: Record<string, string> = {};
    (wb.Workbook?.Names ?? []).forEach((n) => {
        if (n.Name && n.Ref) namedRanges[n.Name] = n.Ref;
    });
    return { sheets: wb.SheetNames.slice(), namedRanges };
}

/** Strip currency/formatting noise (OCS `$` cleanup). */
function cleanCell(v: unknown): string {
    if (v === undefined || v === null) return '';
    return String(v).replace(/\$/g, '').trim();
}

/** Resolve "SheetName" / "SheetName!A1:D20" / named range to sheet+range. */
function resolveTarget(
    wb: XLSX.WorkBook,
    namedRanges: Record<string, string>,
    sheet: string,
    range?: string,
): { ws: XLSX.WorkSheet | null; range: XLSX.Range | null } {
    let sheetName = sheet;
    let rangeText = range?.trim() ?? '';
    // Named range: the range value IS a name (or the sheet names one).
    const named = wb.Workbook?.Names?.find((n) => n.Name === rangeText || n.Name === sheetName);
    if (named?.Ref) {
        const [refSheet, refRange] = named.Ref.split('!');
        if (refSheet && refRange) { sheetName = refSheet.replace(/'/g, ''); rangeText = refRange; }
        else if (!refRange && refSheet) rangeText = refSheet;
    } else if (sheetName.includes('!')) {
        const [s, r] = sheetName.split('!');
        sheetName = s.replace(/'/g, '');
        if (r) rangeText = r;
    }
    const ws = wb.Sheets[sheetName] ?? null;
    if (!ws) return { ws: null, range: null };
    const parsed = rangeText ? XLSX.utils.decode_range(rangeText) : null;
    return { ws, range: parsed };
}

/**
 * Read a sheet (optionally a named range or A1:D20 range) into a string
 * matrix. Empty rows collapse like the CSV path (parseCsv filter).
 */
export function workbookRows(
    data: ArrayBuffer,
    sheet: string,
    range?: string,
): string[][] {
    const wb = loadWorkbook(data);
    const namedRanges: Record<string, string> = {};
    (wb.Workbook?.Names ?? []).forEach((n) => { if (n.Name && n.Ref) namedRanges[n.Name] = n.Ref; });
    const { ws, range: parsed } = resolveTarget(wb, namedRanges, sheet, range);
    if (!ws) return [];
    const matrix = XLSX.utils.sheet_to_json<unknown[]>(ws, {
        header: 1,
        raw: false,
        defval: '',
        // Range bounds (decoded A1:D20) when provided.
        ...(parsed ? {} : {}),
    });
    let rows = matrix.map((row) => row.map(cleanCell));
    if (parsed) {
        rows = rows.slice(parsed.s.r, parsed.e.r + 1).map((row) =>
            row.slice(parsed.s.c, parsed.e.c + 1));
    }
    return rows.filter((r) => r.some((c) => c !== ''));
}
